// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Shield } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface RLHoopsStandingsTableProps {
  ageGroup: string;
  season?: string;
}

const rankAccents: Record<number, string> = {
  1: 'border-l-4 border-l-yellow-500',
  2: 'border-l-4 border-l-gray-400',
  3: 'border-l-4 border-l-amber-700',
};

const RLHoopsStandingsTable = ({ ageGroup, season = '2025-2026' }: RLHoopsStandingsTableProps) => {
  const { data: standings, isLoading } = useQuery({
    queryKey: ['rlhoops-standings', ageGroup, season],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rlhoops_team_rankings')
        .select('*')
        .eq('age_group', ageGroup)
        .eq('season', season)
        .order('total_points', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        No standings available yet for this division.
      </p>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/60">
            <TableHead className="w-14 text-center">Rank</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className="w-14 text-center">W</TableHead>
            <TableHead className="w-14 text-center">L</TableHead>
            <TableHead className="hidden w-14 text-center sm:table-cell">GP</TableHead>
            <TableHead className="w-20 text-center">Points</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standings.map((team, idx) => {
            const rank = idx + 1;
            return (
              <TableRow
                key={team.id}
                className={`${rank % 2 === 0 ? 'bg-muted/30' : ''} ${rankAccents[rank] || ''}`}
              >
                <TableCell className="text-center font-semibold">{rank}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {team.logo_url ? (
                      <img
                        src={team.logo_url}
                        alt={team.team_name}
                        className="h-10 w-10 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                        <Shield className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <span className="font-medium">{team.team_name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center">{team.total_wins ?? 0}</TableCell>
                <TableCell className="text-center">{team.total_losses ?? 0}</TableCell>
                <TableCell className="hidden text-center sm:table-cell">
                  {team.games_played ?? 0}
                </TableCell>
                <TableCell className="text-center font-bold">
                  {(team.total_points ?? 0).toFixed(2)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default RLHoopsStandingsTable;
